import React, { useCallback, useEffect, useState } from "react";
import { SelectList } from "../../../shared/inputs/SelectList";
import { Pill } from "../../../shared/text/Pill.styled";
import { useProject } from "../../../context/ProjectContext";
import { useBanner } from "../../../context/BannerContext";
import { useDatasources } from "../../../context/DatasourceContext";
import { useDrawerActions } from "../../../context/DrawerContext";
import { useEstimateActions } from "../../../hooks/useEstimateActions";
import { useEstimateLoader } from "../../../hooks/useEstimateLoader";
import { Estimate } from "../../../../interfaces/Estimate";
import { ESTIMATES_EMPTY, ESTIMATES_LOAD_ERROR } from "../../../constants";

export const NO_MILESTONE_NAME = "No Milestone";

type EstimateWithMilestone = Estimate & {
  milestoneName: string;
  milestoneProjectedStart: string | null;
};

const milestoneLookupKey = (value: unknown): string => {
  const normalized = String(value ?? "").trim();
  const lower = normalized.toLowerCase();
  if (normalized === "" || lower === "null" || lower === "undefined") {
    return "";
  }
  return normalized;
};

const EstimatesList: React.FC = () => {
  const { selectedProject, selectedEstimate } = useProject();
  const { getEstimatesByProjectId, getMilestonesByProjectId } = useEstimateActions();
  const { addDatasource } = useDatasources();
  const { closeDrawer } = useDrawerActions();
  const { loadEstimate } = useEstimateLoader();
  const { showBanner } = useBanner();
  const [estimates, setEstimates] = useState<EstimateWithMilestone[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const projectId = selectedProject?.id;

  useEffect(() => {
    if (!projectId) return;

    let isCurrent = true;

    const loadEstimates = async () => {
      setLoading(true);
      try {
        const [estimateList, milestones] = await Promise.all([
          getEstimatesByProjectId(projectId),
          getMilestonesByProjectId(projectId),
        ]);

        const milestoneNames = milestones.reduce(
          (acc, milestone) => {
            acc[milestone.id] = milestone.name;
            return acc;
          },
          {} as Record<string, string>
        );
        milestoneNames[""] = NO_MILESTONE_NAME;

        const decorated: EstimateWithMilestone[] = estimateList.map((estimate) => {
          const key = milestoneLookupKey(estimate.milestone);
          return {
            ...estimate,
            milestoneName: milestoneNames[key] ?? NO_MILESTONE_NAME,
            milestoneProjectedStart: milestones.find((milestone) => milestone.id === key)?.projected_start ?? null,
          };
        });

        decorated.sort((a, b) => {
          const startA = a.milestoneProjectedStart ? new Date(a.milestoneProjectedStart).getTime() : -Infinity;
          const startB = b.milestoneProjectedStart ? new Date(b.milestoneProjectedStart).getTime() : -Infinity;
          if (startA !== startB) return startB - startA;
          if (a.primary && !b.primary) return -1;
          if (!a.primary && b.primary) return 1;
          return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
        });

        if (isCurrent) setEstimates(decorated);
      } catch (error) {
        if (isCurrent) showBanner(ESTIMATES_LOAD_ERROR, "error");
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    loadEstimates();

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Registering and closing wait on the load: the progress dialog reports a failure itself, and
  // leaving the list up means the retry is one click away. The loader owns the selection, so a pick
  // that fails no longer replaces the estimate that was already loaded.
  const handleSelectEstimate = useCallback(
    async (estimate: EstimateWithMilestone) => {
      const { dataLoaded } = await loadEstimate(estimate);
      if (!dataLoaded) return;
      addDatasource("ediphi", "estimate");
      closeDrawer();
    },
    [addDatasource, closeDrawer, loadEstimate]
  );

  return (
    <SelectList<EstimateWithMilestone>
      items={estimates.map((estimate) => ({
        id: estimate.id,
        label: estimate.name,
        group: estimate.milestoneName,
        endAdornment: estimate.primary ? <Pill>Primary</Pill> : null,
        data: estimate,
      }))}
      onSelectItem={(estimate) => void handleSelectEstimate(estimate)}
      title="Estimates"
      selectedItemId={selectedEstimate?.id ?? null}
      loading={loading}
      emptyMessage={ESTIMATES_EMPTY}
    />
  );
};

export default EstimatesList;
